// Live diagnostics for a HollowScript editor, checked against the real
// `check()` - a direct port of Cascade's own `diagnostics.ts`, including its
// span-to-position conversion via `@hollow/script`'s published
// `Source.positionAt` (see that file's own doc comment: "the same
// span-to-position conversion reference/editor/decorations.ts used, now
// published ... rather than reimplemented"). The one real difference from
// Cascade's version: this repo's console/editor is a live *creator* surface
// with a real, per-project capability grant (`STUDIO_CAPABILITY_NAMES`,
// `capabilities.ts`), not Cascade's fixed five-capability check-only set, so
// `computeHollowScriptMarkers` takes the granted capability set as a
// parameter instead of hard-coding one.
//
// Returns plain line/column marker data (Monaco's `editor.IMarkerData`
// shape, but expressed with no import from `monaco-editor` at all - see
// `tokenize.ts`'s own doc comment for why that boundary matters here too).
// `startColumn`/`endColumn` are already 1-based via `Source.positionAt`,
// matching Monaco's own marker convention directly with no further
// adjustment.

import { check, Source } from '@hollow/script';
import type { CapabilityBinding, HostCapabilities } from '@hollow/script';
import { HOLLOWSCRIPT_CAPABILITY_CATALOG } from './capabilityCatalog.js';
import type { StudioCapabilityName } from '../capabilities.js';

/** Default entry path for a source with no real file identity of its own
 * (DockConsole's REPL buffer). A named script file (HollowScriptFileEditor,
 * Phase F of the editor-consolidation roadmap's Code workspace) passes its
 * own path instead, so diagnostics correctly identify which file they're
 * against once more than one is open at once. */
const DEFAULT_ENTRY_PATH = 'console.hws';

export interface HollowScriptMarker {
  readonly severity: 'error' | 'warning';
  readonly message: string;
  readonly startLineNumber: number;
  readonly startColumn: number;
  readonly endLineNumber: number;
  readonly endColumn: number;
}

/** Builds the real `HostCapabilities` map to check `source` against: every
 * granted namespace's real type (so an undeclared or ungranted capability
 * is reported exactly the way a real `runScript` would reject it), taken
 * straight from `HOLLOWSCRIPT_CAPABILITY_CATALOG` rather than re-derived. */
function checkCapabilitiesFor(granted: ReadonlySet<StudioCapabilityName> | undefined): HostCapabilities {
  if (granted === undefined) return HOLLOWSCRIPT_CAPABILITY_CATALOG;
  const narrowed = new Map<string, CapabilityBinding>();
  for (const [name, binding] of HOLLOWSCRIPT_CAPABILITY_CATALOG) {
    if (granted.has(name as StudioCapabilityName)) narrowed.set(name, binding);
  }
  return narrowed;
}

/** Type-checks `source` against exactly the capabilities the current
 * project/session grants, so a diagnostic here is the same diagnostic a
 * real run would report. Never throws: `check` itself never does (spec.md
 * gate 2). */
export function computeHollowScriptMarkers(
  source: string,
  granted: ReadonlySet<StudioCapabilityName> | undefined,
  entryPath: string = DEFAULT_ENTRY_PATH,
): readonly HollowScriptMarker[] {
  const { diagnostics } = check(source, checkCapabilitiesFor(granted));
  if (diagnostics.length === 0) return [];

  const positions = new Source(source, entryPath);
  return diagnostics.map((diagnostic) => {
    const from = Math.min(diagnostic.span.start, source.length);
    // A zero-width span (most often an end-of-file diagnostic) still gets at
    // least one character underlined, matching Cascade's own
    // `decorationsForSpan`/`checkHollowScript` treatment of the same case.
    const to = Math.min(Math.max(from + 1, diagnostic.span.end), source.length);
    const start = positions.positionAt(from);
    const end = positions.positionAt(to);
    return {
      severity: diagnostic.severity,
      message: diagnostic.message,
      startLineNumber: start.line,
      startColumn: start.column,
      endLineNumber: end.line,
      endColumn: end.column,
    };
  });
}
